
import React, { Suspense, useState, useEffect } from "react";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { RefreshCcw } from "lucide-react";

// Define a wrapper component to handle the recharts module structure
const ChartComponentsWrapper = (rechartsModule: any) => {
  // Return a component that passes down the recharts exports
  return function RechartsWrapper({ children }: { children: (recharts: any) => React.ReactNode }) {
    return <>{children(rechartsModule)}</>;
  };
};

// Lazy load recharts components to avoid bundling issues
const RechartsDynamicImport = React.lazy(() => 
  import('recharts')
    .then(module => {
      // Convert the module to the expected format that React.lazy requires
      return { 
        default: ChartComponentsWrapper(module)
      };
    })
    .catch(err => {
      console.error("Failed to load recharts:", err);
      // Return a minimal module with mock components
      const mockRecharts = {
        ResponsiveContainer: (props: any) => <div className="flex h-full items-center justify-center">{props.children || "Chart unavailable"}</div>,
        AreaChart: () => <div>Chart unavailable</div>,
        BarChart: () => <div>Chart unavailable</div>,
        PieChart: () => <div>Chart unavailable</div>,
        LineChart: () => <div>Chart unavailable</div>,
        Pie: () => null,
        Area: () => null,
        Bar: () => null,
        Line: () => null,
        XAxis: () => null,
        YAxis: () => null,
        CartesianGrid: () => null,
        Tooltip: () => null,
        Legend: () => null,
        Cell: () => null
      };
      
      return { 
        default: ChartComponentsWrapper(mockRecharts)
      };
    })
);

type ChartLoaderProps = {
  children: (recharts: any) => React.ReactNode;
  height?: string;
  loadingFallback?: React.ReactNode;
  errorFallback?: React.ReactNode;
};

export const LazyChartLoader = ({ 
  children, 
  height = "h-80",
  loadingFallback,
  errorFallback
}: ChartLoaderProps) => {
  const [retryCount, setRetryCount] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Attempt to preload recharts once mounted
    import('recharts')
      .then(() => setIsLoaded(true))
      .catch(err => console.error("Failed to preload recharts:", err));
  }, [retryCount]);

  const handleRetry = () => {
    setRetryCount(prev => prev + 1);
  };

  const defaultLoadingFallback = (
    <div className={`flex items-center justify-center ${height}`}>
      <Skeleton className="h-64 w-full" />
    </div>
  );

  const defaultErrorFallback = (
    <div className={`flex flex-col items-center justify-center gap-4 ${height} p-4 border border-dashed border-gray-300 rounded-lg bg-gray-50`}>
      <p className="text-sm text-gray-500 text-center">Unable to load chart component</p>
      <Button size="sm" variant="outline" onClick={handleRetry}>
        <RefreshCcw className="h-4 w-4 mr-2" />
        Retry
      </Button>
    </div>
  );

  return (
    <ErrorBoundary
      fallback={errorFallback || defaultErrorFallback}
      onReset={handleRetry}
      componentName="Chart"
    >
      <Suspense fallback={loadingFallback || defaultLoadingFallback}>
        <RechartsDynamicImport>
          {children}
        </RechartsDynamicImport>
      </Suspense>
    </ErrorBoundary>
  );
};

// Simplified chart card component with error handling
export const ChartCard = ({ 
  title, 
  description, 
  children, 
  height = "h-80" 
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
  height?: string;
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className={height}>
        <ErrorBoundary componentName={`Chart: ${title}`}>
          {children}
        </ErrorBoundary>
      </CardContent>
    </Card>
  );
};
